import { CssBaseline, ThemeProvider, darken } from "@mui/material";
import { createContext, useEffect, useState, useContext } from "react";
import { muiTheme } from "./mui-theme";
import { getMultitenant } from "../api/multitenant";
import { getSubdomain } from "../helpers/getSubdomain";
import { useLocation } from "react-router-dom";

const TenantThemeContext = createContext();

export function TenantThemeProvider(props) {
  const { pathname } = useLocation();
  const [theme, setTheme] = useState({
    muiTheme,
    siTheme: {
      backgroundColor: "#F7F8FF",
      primaryColor: "#6675FF",
      horizontalLogoUrl:
        "https://cdn.shipinsure.io/logos/shipinsure-horizontal-purple.svg",
      isPrimaryTheme: true,
    },
  });
  const [themeStatus, setThemeStatus] = useState({
    isLoading: true,
    isLoaded: false,
  });

  // this state gives every component that consumes this provider
  // to control its own top-level loading state; (Claims for example)
  const [isAppLoading, setIsAppLoading] = useState(false);

  useEffect(() => {
    // onboarding should be ShipInsure specific theme
    // so don't fetch theme in this scenario;
    // home page for now until we get other whitelabel elements (url, display name)
    // once theme is loaded, we don't expect to fetch again, so we introduce a theme is loaded state
    if (
      pathname === "/" ||
      pathname.includes("/onboarding") ||
      themeStatus.themeIsLoaded
    ) {
      setThemeStatus({
        isLoading: false,
        isLoaded: true,
      });
      return;
    }

    const fetchTheme = async () => {
      try {
        const response = await getMultitenant({ subdomain: getSubdomain() });

        if (!response.data.is_primary) {
          setTheme((prevState) => ({
            muiTheme: {
              ...prevState.muiTheme,
              palette: {
                ...prevState.muiTheme.palette,
                primary: {
                  ...prevState.muiTheme.palette.primary,
                  main: response.data.primary_color,
                  dark: darken(response.data.primary_color, 0.2),
                },
              },
            },
            siTheme: {
              ...prevState.siTheme,
              primaryColor: response.data.primary_color,
              horizontalLogoUrl: response.data.horizontal_logo,
              isPrimaryTheme: false,
            },
          }));
        }
      } finally {
        setThemeStatus({
          isLoading: false,
          isLoaded: true,
        });
      }
    };

    fetchTheme();
  }, [pathname]);

  return (
    <TenantThemeContext.Provider
      value={{
        siTheme: theme.siTheme,
        isThemeLoading: themeStatus.isLoading,
        isAppLoading,
        setIsAppLoading,
      }}
    >
      <CssBaseline />
      <ThemeProvider theme={theme.muiTheme}>{props.children}</ThemeProvider>
    </TenantThemeContext.Provider>
  );
}

export const useTenantTheme = () => useContext(TenantThemeContext);
